import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChangeUserPassword } from 'src/app/models/changePassword.model';
import { AccountService } from 'src/app/services/account.service';
import { TranslateService } from 'src/app/services/translate.service';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { meetsPasswordPolicy } from 'src/app/shared/utils/password-policy.util';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';

type PasswordField = keyof ChangeUserPassword;

interface ChangePasswordForm {
    oldPassword: FormControl<string>;
    newPassword: FormControl<string>;
    confirmPassword: FormControl<string>;
}

@Component({
    standalone: false,
    selector: 'app-account-change-password',
    templateUrl: './account-change-password.component.html',
    styleUrls: ['./account-change-password.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class AccountChangePasswordComponent implements OnInit, OnDestroy {
    @Output() cancelled = new EventEmitter<void>();
    @Output() updated = new EventEmitter<void>();

    readonly fields = {
        oldPassword: nameOf<ChangeUserPassword>((x) => x.oldPassword),
        newPassword: nameOf<ChangeUserPassword>((x) => x.newPassword),
        confirmPassword: nameOf<ChangeUserPassword>((x) => x.confirmPassword),
    };
    readonly trackByIndex = trackByIndex;

    form!: FormGroup<ChangePasswordForm>;
    visibleFields: Record<PasswordField, boolean> = { oldPassword: false, newPassword: false, confirmPassword: false };
    newPassword = '';
    newPasswordInvalid = false;
    passwordsMatch = false;
    passwordsMismatch = false;
    canSubmit = false;
    submitting = false;
    serverErrors: string[] = [];

    private readonly destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private accountService: AccountService,
        private translateService: TranslateService,
    ) {}

    ngOnInit(): void {
        this.form = this.fb.nonNullable.group({
            oldPassword: '',
            newPassword: '',
            confirmPassword: '',
        });
        this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.refreshState());
        this.form.controls.oldPassword.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => (this.serverErrors = []));
    }

    toggleVisibility(field: PasswordField): void {
        this.visibleFields = { ...this.visibleFields, [field]: !this.visibleFields[field] };
    }

    onEnter(event: Event): void {
        event.preventDefault();
        this.submit();
    }

    submit(): void {
        if (!this.canSubmit) return;

        this.submitting = true;
        this.serverErrors = [];
        this.refreshState();
        this.accountService
            .changeUserPassword(this.form.getRawValue())
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.submitting = false;
                    this.updated.emit();
                },
                error: (err: HttpErrorResponse) => {
                    this.submitting = false;
                    this.serverErrors = this.extractServerErrors(err);
                    this.refreshState();
                },
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private refreshState(): void {
        const { oldPassword, newPassword, confirmPassword } = this.form.getRawValue();
        this.newPassword = newPassword;
        this.newPasswordInvalid = newPassword.length > 0 && !meetsPasswordPolicy(newPassword);
        this.passwordsMatch = confirmPassword.length > 0 && confirmPassword === newPassword;
        this.passwordsMismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;
        this.canSubmit =
            oldPassword.length > 0 &&
            meetsPasswordPolicy(newPassword) &&
            this.passwordsMatch &&
            !this.submitting;
    }

    private extractServerErrors(err: HttpErrorResponse): string[] {
        const message = typeof err.error?.message === 'string' ? (err.error.message as string) : '';
        const lines = message
            .split(/<br\s*\/?>/i)
            .map((line) => line.trim())
            .filter((line) => line.length > 0);
        return lines.length
            ? lines
            : [this.translateService.getDynamicTranslation('Failed to update password. Please enter the correct old password.')];
    }
}
